import { createUnifiedTheme, palettes } from '@backstage/theme';

// TD Bank brand colors
// Primary green from TD's brand guidelines
const tdGreen = '#00A950';  // TD's signature green
const tdDarkGreen = '#007A3E';  // Darker shade for hover states
const tdLightGreen = '#4FBF7F';  // Lighter shade

// TD neutral colors
const tdWhite = '#FFFFFF';
const tdBlack = '#000000';
const tdMediumGrey = '#666666';
const tdLightGrey = '#F5F5F5';
const tdBorderGrey = '#E0E0E0';
const tdTextGrey = '#757575';

// TD accent colors
const tdBlue = '#003DA5';  // Secondary blue for links
const tdOrange = '#FF6900';  // Alert/warning color
const tdRed = '#C8102E';  // Error color

export const tdLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: tdGreen,
      light: tdLightGreen,
      dark: tdDarkGreen,
      contrastText: tdWhite,
    },
    secondary: {
      main: tdBlue,
      light: '#4D7FCC',
      dark: '#002E7D',
      contrastText: tdWhite,
    },
    background: {
      default: tdWhite,
      paper: tdLightGrey,
    },
    text: {
      primary: tdBlack,
      secondary: tdMediumGrey,
    },
    navigation: {
      background: tdGreen,
      indicator: tdWhite,
      color: tdWhite,
      selectedColor: tdWhite,
      navItem: {
        hoverBackground: 'rgba(255, 255, 255, 0.15)',
      },
    },
    status: {
      ok: tdGreen,
      warning: tdOrange,
      error: tdRed,
      running: tdBlue,
      pending: tdTextGrey,
      aborted: '#9E9E9E',
    },
    divider: tdBorderGrey,
  },
  fontFamily: '"TD Replica", "Helvetica Neue", Helvetica, Arial, sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    documentation: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    tool: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    service: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    website: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    library: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    other: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    app: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    apis: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    card: {
      fontColor: tdBlack,
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '6px',
          fontWeight: 600,
          fontSize: '16px',
          padding: '10px 24px',
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          backgroundColor: tdGreen,
          color: tdWhite,
          '&:hover': {
            backgroundColor: tdDarkGreen,
            boxShadow: '0 4px 12px rgba(0, 169, 80, 0.25)',
          },
          '&:active': {
            backgroundColor: tdDarkGreen,
          },
        },
        containedSecondary: {
          backgroundColor: tdBlue,
          color: tdWhite,
          '&:hover': {
            backgroundColor: '#002E7D',
            boxShadow: '0 4px 12px rgba(0, 61, 165, 0.25)',
          },
        },
        outlined: {
          borderColor: tdGreen,
          color: tdGreen,
          borderWidth: '2px',
          '&:hover': {
            borderColor: tdDarkGreen,
            backgroundColor: 'rgba(0, 169, 80, 0.04)',
            borderWidth: '2px',
          },
        },
        text: {
          color: tdGreen,
          '&:hover': {
            backgroundColor: 'rgba(0, 169, 80, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: 'none',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: 'rgba(0, 169, 80, 0.1)',
          color: tdGreen,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: '48px',
          fontWeight: 600,
          fontSize: '14px',
          '&.Mui-selected': {
            color: tdGreen,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tdBorderGrey}`,
        },
        head: {
          fontWeight: 600,
          backgroundColor: tdLightGrey,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontWeight: 700,
          color: tdBlack,
          letterSpacing: '-0.02em',
        },
        h2: {
          fontWeight: 700,
          color: tdBlack,
          letterSpacing: '-0.01em',
        },
        h3: {
          fontWeight: 600,
          color: tdBlack,
        },
        h4: {
          fontWeight: 600,
          color: tdBlack,
        },
        h5: {
          fontWeight: 500,
          color: tdBlack,
        },
        h6: {
          fontWeight: 500,
          color: tdBlack,
        },
        body1: {
          fontSize: '16px',
          lineHeight: 1.6,
        },
        body2: {
          fontSize: '14px',
          lineHeight: 1.5,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: tdBlue,
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'none',
            color: '#002E7D',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: tdGreen,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tdWhite,
          borderRight: `1px solid ${tdBorderGrey}`,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 169, 80, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 169, 80, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(0, 169, 80, 0.12)',
            },
          },
        },
      },
    },
  },
});

export const tdDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: tdGreen,
      light: tdLightGreen,
      dark: tdDarkGreen,
      contrastText: tdWhite,
    },
    secondary: {
      main: '#4D7FCC',  // Lighter blue for dark mode
      light: '#7FA3D9',
      dark: tdBlue,
      contrastText: tdWhite,
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B3B3B3',
    },
    navigation: {
      background: '#1E1E1E',
      indicator: tdGreen,
      color: '#FFFFFF',
      selectedColor: tdGreen,
      navItem: {
        hoverBackground: 'rgba(0, 169, 80, 0.1)',
      },
    },
    status: {
      ok: tdGreen,
      warning: tdOrange,
      error: '#FF6B6B',  // Lighter red for dark mode
      running: '#4D7FCC',
      pending: '#808080',
      aborted: '#606060',
    },
    divider: '#3C3C3C',
  },
  fontFamily: '"TD Replica", "Helvetica Neue", Helvetica, Arial, sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    documentation: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    tool: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    service: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    website: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    library: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    other: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    app: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    apis: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    card: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#121212',
        },
        html: {
          backgroundColor: '#121212',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '6px',
          fontWeight: 600,
          fontSize: '16px',
          padding: '10px 24px',
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          backgroundColor: tdGreen,
          color: tdWhite,
          '&:hover': {
            backgroundColor: tdLightGreen,
            boxShadow: '0 4px 12px rgba(0, 169, 80, 0.35)',
          },
        },
        containedSecondary: {
          backgroundColor: '#4D7FCC',
          color: tdWhite,
          '&:hover': {
            backgroundColor: '#7FA3D9',
            boxShadow: '0 4px 12px rgba(77, 127, 204, 0.35)',
          },
        },
        outlined: {
          borderColor: tdGreen,
          color: tdGreen,
          borderWidth: '2px',
          '&:hover': {
            borderColor: tdLightGreen,
            backgroundColor: 'rgba(0, 169, 80, 0.08)',
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: '#1E1E1E',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
          border: '1px solid #3C3C3C',
          '&:hover': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: 'rgba(0, 169, 80, 0.2)',
          color: tdLightGreen,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: '#1E1E1E',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: '48px',
          fontWeight: 600,
          fontSize: '14px',
          '&.Mui-selected': {
            color: tdGreen,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #3C3C3C',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#2A2A2A',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontWeight: 700,
          letterSpacing: '-0.02em',
        },
        h2: {
          fontWeight: 700,
          letterSpacing: '-0.01em',
        },
        h3: {
          fontWeight: 600,
        },
        h4: {
          fontWeight: 600,
        },
        h5: {
          fontWeight: 500,
        },
        h6: {
          fontWeight: 500,
        },
        body1: {
          fontSize: '16px',
          lineHeight: 1.6,
        },
        body2: {
          fontSize: '14px',
          lineHeight: 1.5,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#4D7FCC',
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'none',
            color: '#7FA3D9',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#1E1E1E',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E1E1E',
          borderRight: '1px solid #3C3C3C',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 169, 80, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 169, 80, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(0, 169, 80, 0.2)',
            },
          },
        },
      },
    },
  },
});